<script setup>
import BaseModal from "@/components/global-themed/BaseModal.vue";
import {computed, ref} from "vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import ErrorMessages from "@/components/global/ErrorMessages.vue";
import {useMutation} from "@tanstack/vue-query";
import axios from "axios";
import SweetToast from "@/SweetToast";

const props = defineProps({
  unselectedKeywords: {
    type: Array,
    required: true,
  },
  showModal: {
    type: Boolean,
    required: true,
  }
});

const emit = defineEmits(['close', 'submitted']);

const feedback = ref('');

const errors = computed(() => error.value?.response?.data?.errors);

const handleSubmit = () => {
  sendKeywordFeedback();
}

const {mutate: sendKeywordFeedback, error, isPending} = useMutation({
  mutationFn: async () => await axios.post('/ajax/v2/ai-onboarding/keywords/feedback', {
    feedback: feedback.value,
    keywords: props.unselectedKeywords,
  }),
  onSuccess: () => {
    SweetToast.fire({
      icon: 'success',
      title: 'Thanks for submitting your feedback, we appreciate it!',
    });
    emit('submitted');
  },
  onError: ({response}) => {
      SweetToast.fire({
        icon: 'error',
        title: response.data.message ?? 'Something went wrong',
      });
  },
});

</script>

<template>
  <Teleport to=".tailwind">
    <BaseModal
      v-if="showModal"
      modal-class="tw-max-w-[800px]"
      @close="() => emit('close')"
    >
      <div class="tw-p-4 tw-pt-0">
        <h2 class="tw-text-2.5xl tw-font-bold tw-mb-4">
          Help me fine-tune your keyword strategy
        </h2>
        <p class="mb-2">
          I noticed you removed several keywords from the list. That’s totally fine—it helps me understand your focus better.
        </p>
        <p class="mb-4">
          Could you share a bit about your thinking? Whether you’re narrowing down your target or have another reason, your feedback will help me build an even sharper SEO strategy for you and will also be passed on to my managers.
        </p>
        <form
          action=""
          @submit.prevent="handleSubmit"
        >
          <div class="mb-4">
            <textarea
              id="keywordFeedback"
              v-model="feedback"
              class="tw-border-2 tw-border-grey-mid tw-flex tw-text-black tw-self-stretch focus-within:tw-border-blue tw-rounded-lg tw-px-6 tw-py-3 tw-text-lg tw-w-full tw-resize-none"
              placeholder="Enter feedback here..."
              name="keywordFeedback"
              rows="4"
            />
            <ErrorMessages :errors="errors?.feedback ?? []" />
          </div>
          <div class="tw-flex tw-content-end">
            <DefaultButton
              variant="primary"
              type="submit"
              :disabled="isPending || !feedback"
              :loading="isPending"
            >
              Submit
            </DefaultButton>
          </div>
        </form>
      </div>
    </BaseModal>
  </Teleport>
</template>
